import { useMemo } from 'react';

import { Checkbox } from '@/lib/base/forms/checkbox';
import { Select } from '@/lib/base/forms/select/select';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';

import type { ActiveCampaignListItem } from '../../types';

interface WizardImportStepProps {
  lists: ActiveCampaignListItem[];
  isLoading: boolean;
  isError: boolean;
  selectedListId: string;
  onSelectListChange: (listId: string) => void;
  importEnabled: boolean;
  onImportToggleChange: (enabled: boolean) => void;
  isSaving: boolean;
  errorMessage: string | null;
}

export function WizardImportStep({
  lists,
  isLoading,
  isError,
  selectedListId,
  onSelectListChange,
  importEnabled,
  onImportToggleChange,
  isSaving,
  errorMessage,
}: WizardImportStepProps) {
  // Select's items expect { id, label }; the API's ActiveCampaignListItem
  // shape is { id, name } -- adapt at the render boundary.
  const selectItems = useMemo(
    () => lists.map(list => ({ id: list.id, label: list.name })),
    [lists]
  );

  if (isLoading) {
    return (
      <div className='flex justify-center py-10'>
        <LoadingIndicator size='sm' />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex flex-col gap-2'>
        <label
          htmlFor='ac-default-list-select'
          className='text-body-small font-medium text-[var(--color-text-primary)]'
        >
          Default list
        </label>
        <p className='text-body-small text-[var(--color-text-secondary)]'>
          New ActiveCampaign contacts created from Ahoy are added to this list.
          You can leave this unset and choose one later.
        </p>
        {isError ? (
          <p className='text-body-small text-[var(--color-text-error-primary)]'>
            Couldn&apos;t load your ActiveCampaign lists — please try again.
          </p>
        ) : (
          <Select
            id='ac-default-list-select'
            aria-label='Default ActiveCampaign list'
            placeholder='No default list'
            selectedKey={selectedListId || null}
            onSelectionChange={key => onSelectListChange(String(key ?? ''))}
            items={selectItems}
            isDisabled={isSaving}
          >
            {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
          </Select>
        )}
      </div>

      <div className='flex flex-col gap-2'>
        <Checkbox
          label='Sync my existing contacts now'
          hint='Sends a one-time copy of your existing Ahoy contacts to ActiveCampaign. New contacts sync automatically either way.'
          isSelected={importEnabled}
          onChange={onImportToggleChange}
          isDisabled={isSaving}
        />
      </div>

      {errorMessage && (
        <p className='text-body-small text-[var(--color-text-error-primary)]'>
          {errorMessage}
        </p>
      )}
    </div>
  );
}
